/**
 * Smart logger for app-wide Mercure subscriptions.
 * Batches subscription logs to reduce console noise.
 */

type LogLevel = "info" | "success" | "warn" | "error";

const isDev = import.meta.env?.DEV === true;

function mercureAppLog(message: string, data: unknown = null, type: LogLevel = "info") {
  if (!isDev) return;

  const prefix = `%c AppMercure `;
  const baseStyle = "padding: 2px 4px; border-radius: 3px; font-weight: bold; color: white;";

  const styles: Record<LogLevel, string> = {
    info: `${baseStyle} background: #9b59b6;`,
    success: `${baseStyle} background: #2ecc71;`,
    warn: `${baseStyle} background: #f1c40f; color: #333;`,
    error: `${baseStyle} background: #e74c3c;`,
  };

  const args: unknown[] = [prefix, styles[type], message];
  if (data !== null) args.push(data);

  console[type === "success" || type === "info" ? "log" : type](...args);
}

export function logBatchSubscribe(topics: string[], userId: number) {
  if (!topics || topics.length === 0) return;
  mercureAppLog(
    `Subscribed to ${topics.length} app topic${topics.length > 1 ? "s" : ""}`,
    { userId, topics },
    "success",
  );
}

export function logBatchUnsubscribe(topicCount: number, userId: number) {
  if (topicCount === 0) return;
  mercureAppLog(
    `Unsubscribed from ${topicCount} app topic${topicCount > 1 ? "s" : ""}`,
    { userId },
    "warn",
  );
}

export function logReady(topicCount: number, userId: number) {
  mercureAppLog(`✅ All app subscriptions ready (${topicCount} topics)`, { userId }, "success");
}

export function logError(context: string, error: Error | null, userId: number) {
  mercureAppLog(`❌ ${context}`, { userId, error: error?.message }, "error");
}

export { mercureAppLog };
export default mercureAppLog;
